import { Promise } from 'rsvp';
import { assert } from '@ember/debug';
import animateScrollTo from 'animated-scroll-to';
const ANIMATED_SCROLL_DURATION = 750;

/**
 * Determines if the given element is currently in the viewport. An offset may
 * be provided to add to the top of the element during the calculation.
 *
 * @function isElementInViewport
 * @param {HTMLElement} element the element to test
 * @returns {Boolean}
 */
export function isElementInViewport(element, {
  topOffset
} = {
  topOffset: 0
}) {
  const {
    top,
    bottom
  } = element.getBoundingClientRect();
  return top - topOffset >= 0 && bottom <= document.documentElement.clientHeight;
}

/**
 * Returns a default scrollable element. If you are using `scrollToElement` but
 * do not have a specific element to scroll this function should be used to
 * get a scrollable element.
 *
 * In an ember test environment the test container will be returned.
 *
 * @function defaultScrollable
 * @returns {HTMLElement} Element that can be scrolled.
 */
export function defaultScrollable() {
  const testingContainer = document.getElementById('ember-testing-container');
  if (testingContainer) {
    return testingContainer;
  }

  // On IE scrollingElement is not defined. In this case fallback to
  // documentElement which will be the html tag.
  return document.scrollingElement || document.documentElement;
}

/**
 * Resets scroll position on scrollable.
 *
 * @function resetScroll
 * @param {HTMLElement} scrollable A HTMLElement to scroll
 *   representing a scrollable part of the document
 * @returns {void}
 */
export function resetScroll(scrollable) {
  assert('No element to scroll', scrollable instanceof HTMLElement);
  scrollable.scrollTop = 0;
}

/**
 * Scrolls the page so that element is in the viewport. If the element is
 * completely in the viewport no scrolling will take place.
 *
 * @function scrollToElement
 * @param {HTMLElement} element Element to scroll to
 * @param {HTMLElement} scrollable Element to scroll
 * @param {Object} [options]
 * @param {Number} [options.topOffset] An offset to apply to the top of element when
 *   calculating scroll position and whether element is within the viewport
 * @returns {Promise} A promise which resolves when the scrolling is complete.
 *   If no scrolling takes place the promise will immediately resolve.
 */
export function scrollToElement(element, scrollable, {
  topOffset
} = {
  topOffset: 0
}) {
  assert('No element to scroll to', element instanceof HTMLElement);
  assert('No element to scroll', scrollable instanceof HTMLElement);
  assert('Top offset must be a number', typeof topOffset === 'number');

  // Only scroll if element is not fully visible.
  if (!isElementInViewport(element, {
    topOffset
  })) {
    return animateScrollTo(element, {
      elementToScroll: scrollable,
      speed: 1000,
      maxDuration: ANIMATED_SCROLL_DURATION,
      verticalOffset: -topOffset,
      // easeInOutCubic. https://github.com/Stanko/animated-scroll-to#easing
      easing: t => {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      }
    });
  } else {
    // Provide a resolved promise so that this function returns a promise in
    // all cases.
    return new Promise(resolve => {
      resolve();
    });
  }
}